import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';
import { STATIC_BASE_URL } from '../config/api';

const Home = () => {
  const [thematicAreas, setThematicAreas] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [partners, setPartners] = useState([]);
  const [heroImages, setHeroImages] = useState([]);
  const [stats, setStats] = useState([]);
  const [mission, setMission] = useState({});
  const [vision, setVision] = useState({});
  const [values, setValues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [aboutSection, setAboutSection] = useState({});
  const [partnerStartIndex, setPartnerStartIndex] = useState(0);

  // Partner type configuration - UI mapping only
  const partnerTypeConfig = {
    'donor': { 
      color: 'primary', 
      icon: 'bi-cash-coin',
      label: 'Donor Partner'
    },
    'implementing': { 
      color: 'success', 
      icon: 'bi-handshake',
      label: 'Implementing Partner'
    },
    'government': { 
      color: 'info', 
      icon: 'bi-building',
      label: 'Government Partner'
    },
    'community': { 
      color: 'warning', 
      icon: 'bi-people-fill',
      label: 'Community Partner'
    },
    'academic': { 
      color: 'dark', 
      icon: 'bi-mortarboard',
      label: 'Academic Partner'
    },
    'corporate': { 
      color: 'secondary', 
      icon: 'bi-briefcase',
      label: 'Corporate Partner'
    }
  };

  useEffect(() => {
    fetchHomeData();
    setIsVisible(true);
    
    // Auto-slide hero images
    const heroInterval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % (heroImages.length || 1));
    }, 5000);

    return () => {
      clearInterval(heroInterval);
    };
  }, [heroImages.length]);

  const fetchHomeData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(API_ENDPOINTS.HOME, { timeout: 8000 });
      if (res.data?.success && res.data?.data) {
        const d = res.data.data;
        setThematicAreas(d.thematicAreas || []);
        setUpdates(d.updates || []);
        setPartners(d.partners || []);
        setHeroImages((d.heroImages || []).map((img) => img.image_path || img));
        setStats(Array.isArray(d.stats) ? d.stats : []);
        setAboutSection(d.aboutSection || {});
        setMission(d.mission || {});
        setVision(d.vision || {});
        setValues(Array.isArray(d.values) ? d.values : []);
      } else {
        await Promise.all([
          fetchThematicAreas(),
          fetchUpdates(),
          fetchPartners(),
          fetchHeroImages(),
          fetchStats(),
          fetchMissionVisionValues(),
          fetchAboutSection()
        ]);
      }
    } catch (err) {
      console.warn('Home API unavailable, falling back to individual requests:', err.message);
      await Promise.all([
        fetchThematicAreas(),
        fetchUpdates(),
        fetchPartners(),
        fetchHeroImages(),
        fetchStats(),
        fetchMissionVisionValues(),
        fetchAboutSection()
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAboutSection = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.SETTINGS);
      if (response.data.success) {
        const settings = response.data.data;
        setAboutSection({
          title: settings.homepage_about_title || 'About SIKOM',
          subtitle: settings.homepage_about_subtitle || 'Building Lasting Peace for Sustainable Development',
          content: settings.homepage_about_content || 'SIKOM Peace Network for Development is a Non-Governmental Organization established in 2001 as a self-help group and registered in 2006 as a national NGO in Kenya.',
          image: settings.homepage_about_image || '',
          enabled: settings.homepage_about_enabled !== '0' && settings.homepage_about_enabled !== 'false'
        });
      }
    } catch (error) {
      console.error('Error fetching about section:', error);
      setAboutSection({
        title: 'About SIKOM',
        subtitle: 'Building Lasting Peace for Sustainable Development',
        content: 'SIKOM Peace Network for Development is a Non-Governmental Organization established in 2001 as a self-help group and registered in 2006 as a national NGO in Kenya.',
        image: '',
        enabled: true
      });
    }
  };

  const fetchThematicAreas = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.THEMATIC_AREAS, {
        params: { limit: 6, is_active: 1 }
      });
      if (response.data.success) {
        setThematicAreas(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching thematic areas:', error);
      setThematicAreas([]);
    }
  };

  const fetchUpdates = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.UPDATES, {
        params: { limit: 3, is_active: 1 }
      });
      if (response.data.success) {
        setUpdates(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching updates:', error);
      setUpdates([]);
    }
  };

  const fetchPartners = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.PARTNERS, {
        params: { is_active: 1 },
        timeout: 5000
      });
      let partnersData = [];
      if (response.data?.success && response.data?.data) {
        partnersData = response.data.data;
      } else if (Array.isArray(response.data)) {
        partnersData = response.data;
      } else if (response.data?.partners) {
        partnersData = response.data.partners;
      }
      const active = (partnersData || []).filter(
        (p) => p.is_active === 1 || p.is_active === true || p.is_active === undefined
      );
      const sorted = active.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
      const limited = sorted.slice(0, 8).map((p) => ({
        ...p,
        logo_path: fixLogoPath(p.logo_path || p.logo_url)
      }));
      setPartners(limited);
    } catch (error) {
      console.error('Error fetching partners:', error);
      setPartners([]);
    }
  };

  // Helper function to fix logo paths - RECOMMENDED VERSION
  const fixLogoPath = (path) => {
    if (!path) return `${STATIC_BASE_URL}/images/placeholder-partner.png`;

    // If it's already a full URL, use it
    if (path.startsWith('http://') || path.startsWith('https://')) return path;

    // Ensure no leading slash
    const cleanPath = path.startsWith('/') ? path.slice(1) : path;

    return `${STATIC_BASE_URL}/${cleanPath}`;
  };

  const fetchHeroImages = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.HERO_IMAGES, {
        params: { is_active: 1 }
      });
      if (response.data.success) {
        setHeroImages(response.data.data.map(img => img.image_path));
      } else {
        // Fallback to default images if API returns no data
        setHeroImages([
          '/images/hero1.jpg',
          '/images/hero2.jpg',
          '/images/hero3.jpg'
        ]);
      }
    } catch (error) {
      console.error('Error fetching hero images:', error);
      // Fallback to default images
      setHeroImages([
        '/images/hero1.jpg',
        '/images/hero2.jpg',
        '/images/hero3.jpg'
      ]);
    }
  };

  const defaultStats = [
    { title: "Years of experience", value: "20+", icon: "bi-calendar" },
    { title: "Countries", value: "2", icon: "bi-globe" },
    { title: "Counties coverage", value: "4", icon: "bi-geo-alt" },
    { title: "Beneficiaries", value: "500,000+", icon: "bi-people" },
    { title: "Projects completed", value: "25", icon: "bi-check-circle" }
  ];

  const fetchStats = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.STATS);
      if (response.data.success && Array.isArray(response.data.data)) {
        setStats(response.data.data);
      } else if (response.data.success && response.data.data && typeof response.data.data === 'object') {
        // Settings API returns object - build stats array from stat_* keys
        const s = response.data.data;
        const arr = [
          { title: "Years of experience", value: s.stat_years || "20+", icon: "bi-calendar" },
          { title: "Countries", value: s.stat_countries || "2", icon: "bi-globe" },
          { title: "Counties coverage", value: s.stat_counties || "4", icon: "bi-geo-alt" },
          { title: "Beneficiaries", value: s.stat_beneficiaries || "500,000+", icon: "bi-people" },
          { title: "Projects completed", value: s.stat_projects || "25", icon: "bi-check-circle" }
        ];
        setStats(arr);
      } else {
        setStats(defaultStats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
      setStats(defaultStats);
    }
  };

  const fetchMissionVisionValues = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.ABOUT_SECTIONS, {
        params: { sections: ['mission', 'vision', 'values'] }
      });
      
      if (response.data.success) {
        const data = response.data.data;
        setMission(data.mission || { 
          title: 'Our Mission',
          content: 'To integrate indigenous knowledge and innovative solutions to achieving sustainable development in East Africa.'
        });
        
        setVision(data.vision || { 
          title: 'Our Vision',
          content: 'Wholistic development for healthy and peaceful co-existence in East Africa.'
        });
        
        setValues(data.values || [
          'Respect for human rights',
          'Neutrality and impartiality',
          'Equity and justice',
          'Transparency and accountability',
          'Stewardship and role modelling'
        ]);
      }
    } catch (error) {
      console.error('Error fetching mission/vision/values:', error);
      setMission({ 
        title: 'Our Mission',
        content: 'To integrate indigenous knowledge and innovative solutions to achieving sustainable development in East Africa.'
      });
      setVision({ 
        title: 'Our Vision',
        content: 'Wholistic development for healthy and peaceful co-existence in East Africa.'
      });
      setValues([
        'Respect for human rights',
        'Neutrality and impartiality',
        'Equity and justice',
        'Transparency and accountability',
        'Stewardship and role modelling'
      ]);
    }
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  // Helper function to get partner type info
  const getPartnerTypeInfo = (type) => {
    return partnerTypeConfig[type] || { 
      color: 'secondary', 
      icon: 'bi-building',
      label: 'Partner'
    };
  };

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="text-center">
          <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }} role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading SIKOM...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .hero-section {
          min-height: 100vh;
          position: relative;
          overflow: hidden;
        }

        .hero-slider {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: 0;
        }

        .hero-slide {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-size: cover;
          background-position: center;
          transition: opacity 1.5s ease-in-out, transform 8s ease-out;
        }

        .hero-slide.active {
          animation: kenBurns 8s ease-out;
        }

        @keyframes kenBurns {
          0% { transform: scale(1); }
          100% { transform: scale(1.1); }
        }

        .text-gradient {
          background: linear-gradient(135deg, #ffffff 0%, #e0f2fe 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: gradientShift 3s ease infinite;
        }

        @keyframes gradientShift {
          0%, 100% { filter: brightness(1); }
          50% { filter: brightness(1.2); }
        }

        .hero-content {
          opacity: 0;
          transform: translateY(30px);
        }

        .hero-content.animate-in {
          animation: fadeInUp 1s ease forwards;
        }

        @keyframes fadeInUp {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .subtitle-badge {
          animation: fadeInUp 1s ease 0.2s backwards;
        }

        .hero-title {
          animation: fadeInUp 1s ease 0.4s backwards;
          text-shadow: 2px 4px 8px rgba(0, 0, 0, 0.3);
        }

        .hero-text {
          animation: fadeInUp 1s ease 0.6s backwards;
          text-shadow: 1px 2px 4px rgba(0, 0, 0, 0.2);
        }

        .hero-buttons {
          animation: fadeInUp 1s ease 0.8s backwards;
        }

        .hero-stats {
          animation: fadeInUp 1s ease 1s backwards;
        }

        .cta-btn {
          position: relative;
          overflow: hidden;
          font-weight: 600;
          letter-spacing: 0.5px;
          border: none;
          transition: all 0.4s ease;
        }

        .cta-btn::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
          transition: left 0.5s ease;
        }

        .cta-btn:hover::before {
          left: 100%;
        }

        .cta-btn:hover {
          transform: translateY(-3px);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3) !important;
        }

        .cta-btn-outline {
          position: relative;
          font-weight: 600;
          letter-spacing: 0.5px;
          border: 2px solid white;
          background: transparent;
          transition: all 0.4s ease;
          overflow: hidden;
        }

        .cta-btn-outline::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 0;
          height: 100%;
          background: white;
          transition: width 0.4s ease;
          z-index: -1;
        }

        .cta-btn-outline:hover::before {
          width: 100%;
        }

        .cta-btn-outline:hover {
          color: #0d6efd !important;
          border-color: white;
          transform: translateY(-3px);
          box-shadow: 0 10px 30px rgba(255, 255, 255, 0.3);
        }

        .stat-item {
          animation: slideUp 0.6s ease backwards;
        }

        .stat-item:nth-child(1) { animation-delay: 1s; }
        .stat-item:nth-child(2) { animation-delay: 1.1s; }
        .stat-item:nth-child(3) { animation-delay: 1.2s; }
        .stat-item:nth-child(4) { animation-delay: 1.3s; }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .counter {
          font-size: 2rem;
        }

        .slider-dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          border: 2px solid white;
          background: transparent;
          cursor: pointer;
          transition: all 0.3s ease;
          padding: 0;
        }

        .slider-dot:hover {
          transform: scale(1.2);
          background: rgba(255, 255, 255, 0.5);
        }

        .slider-dot.active {
          width: 40px;
          border-radius: 6px;
          background: white;
        }

        .scroll-indicator {
          animation: bounce 2s infinite;
          font-size: 2rem;
          opacity: 0.8;
        }

        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
          40% { transform: translateY(-10px); }
          60% { transform: translateY(-5px); }
        }

        .cta-btn i {
          transition: transform 0.3s ease;
        }

        .cta-btn:hover i {
          transform: translateX(5px);
        }

        .cta-btn-outline:hover .bi-heart-fill {
          animation: heartbeat 0.6s ease infinite;
        }

        @keyframes heartbeat {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.2); }
        }

        .hover-shadow {
          transition: all 0.3s ease;
        }

        .hover-shadow:hover {
          transform: translateY(-10px);
          box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15) !important;
        }

        .mvv-card {
          transition: all 0.4s ease;
        }

        .mvv-card:hover {
          transform: translateY(-10px);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15) !important;
        }

        .mvv-card:hover .rounded-circle {
          transform: scale(1.1) rotate(5deg);
        }

        .bg-gradient-light {
          background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
        }

        .bg-gradient-primary {
          background: linear-gradient(135deg, #0d6efd 0%, #6c757d 100%);
        }

        .thematic-card {
          transition: all 0.4s ease;
          border-left: 4px solid transparent;
        }

        .thematic-card:hover {
          transform: translateY(-10px);
          border-left-color: #0d6efd;
          box-shadow: 0 15px 40px rgba(13, 110, 253, 0.2) !important;
        }

        .thematic-card i {
          transition: all 0.4s ease;
        }

        .thematic-card:hover i {
          transform: scale(1.2) rotate(5deg);
          color: #0d6efd;
        }

        .stat-counter {
          transition: all 0.3s ease;
        }

        .stat-counter:hover {
          transform: scale(1.1);
        }

        .stat-counter i {
          transition: transform 0.5s ease;
        }

        .stat-counter:hover i {
          transform: rotateY(180deg);
        }

        .update-card {
          transition: all 0.3s ease;
          overflow: hidden;
        }

        .update-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15) !important;
        }

        .update-card .badge {
          transition: all 0.3s ease;
        }

        .update-card:hover .badge {
          transform: scale(1.1);
        }

        .btn-link {
          position: relative;
          text-decoration: none;
          transition: all 0.3s ease;
        }

        .btn-link::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          width: 0;
          height: 2px;
          background: #0d6efd;
          transition: width 0.3s ease;
        }

        .btn-link:hover::after {
          width: 100%;
        }

        .btn-link i {
          transition: transform 0.3s ease;
        }

        .btn-link:hover i {
          transform: translateX(5px);
        }

        .partners-section {
          background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
          position: relative;
          overflow: hidden;
        }

        .partners-carousel-wrapper {
          padding: 2rem 0;
        }

        .partners-carousel-container {
          position: relative;
          padding: 1rem 0;
          overflow: hidden;
        }

        .partners-carousel-scroll {
          display: flex;
          animation: scrollRightToLeft 30s linear infinite;
          will-change: transform;
        }

        .partners-carousel-scroll:hover {
          animation-play-state: paused;
        }

        @keyframes scrollRightToLeft {
          0% {
            transform: translateX(0%);
          }
          100% {
            transform: translateX(-50%);
          }
        }

        .partner-logo-item {
          transition: transform 0.3s ease;
        }

        .partner-logo-item:hover {
          transform: translateY(-5px);
        }

        .partner-logo-card {
          min-height: 180px;
          transition: all 0.3s ease;
          border: 2px solid transparent;
        }

        .partner-logo-card:hover {
          border-color: rgba(13, 110, 253, 0.2);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1) !important;
        }

        .partner-logo-wrapper {
          width: 100%;
        }

        .partner-logo-img {
          max-width: 100%;
          max-height: 80px;
          object-fit: contain;
          filter: grayscale(100%) opacity(0.7);
          transition: all 0.3s ease;
        }

        .partner-logo-card:hover .partner-logo-img {
          filter: grayscale(0%) opacity(1);
          transform: scale(1.05);
        }

        .partner-name-text {
          color: #333;
          transition: color 0.3s ease;
        }

        .partner-logo-card:hover .partner-name-text {
          color: #0d6efd;
        }

        .partner-dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          border: 2px solid #0d6efd;
          background: transparent;
          cursor: pointer;
          transition: all 0.3s ease;
          padding: 0;
        }

        .partner-dot:hover {
          transform: scale(1.2);
          background: rgba(13, 110, 253, 0.2);
        }

        .partner-dot.active {
          background: #0d6efd;
          width: 30px;
          border-radius: 6px;
        }

        .about-image-wrapper {
          position: relative;
          overflow: hidden;
          border-radius: 1rem;
        }

        .about-image-wrapper::before {
          content: '';
          position: absolute;
          top: -50%;
          right: -50%;
          width: 200%;
          height: 200%;
          background: linear-gradient(45deg, transparent, rgba(13, 110, 253, 0.1), transparent);
          transform: rotate(45deg);
          animation: shine 3s infinite;
        }

        @keyframes shine {
          0% { transform: translateX(-100%) translateY(-100%) rotate(45deg); }
          100% { transform: translateX(100%) translateY(100%) rotate(45deg); }
        }

        .partner-card {
          transition: all 0.4s ease;
          background: white;
          border-radius: 12px;
          overflow: hidden;
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 2rem;
          border: 1px solid rgba(0, 0, 0, 0.05);
          position: relative;
        }

        .partner-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, var(--bs-primary), var(--bs-success));
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .partner-card:hover::before {
          opacity: 1;
        }

        .partner-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.12) !important;
          border-color: rgba(13, 110, 253, 0.1);
        }

        .partner-logo-container {
          height: 100px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 1.5rem;
          width: 100%;
        }

        .partner-logo {
          max-width: 180px;
          max-height: 80px;
          object-fit: contain;
          transition: all 0.4s ease;
          filter: grayscale(100%) brightness(0.9);
          opacity: 0.8;
        }

        .partner-card:hover .partner-logo {
          filter: grayscale(0%) brightness(1);
          opacity: 1;
          transform: scale(1.08);
        }

        .partner-name {
          font-size: 1.1rem;
          color: #333;
          text-align: center;
          margin-bottom: 0.75rem;
          font-weight: 600;
          line-height: 1.4;
          min-height: 3em;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .partner-type-badge {
          font-size: 0.75rem;
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          margin-bottom: 1rem;
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
        }

        .partner-description {
          font-size: 0.85rem;
          color: #666;
          text-align: center;
          line-height: 1.5;
          margin-bottom: 1rem;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
          min-height: 4.5em;
        }

        .partner-link {
          text-decoration: none;
          color: inherit;
          display: block;
          width: 100%;
          height: 100%;
        }

        .partner-link:hover {
          text-decoration: none;
          color: inherit;
        }

        .partner-website-link {
          position: absolute;
          bottom: 1rem;
          right: 1rem;
          width: 36px;
          height: 36px;
          background: var(--bs-light);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transform: translateY(10px);
          transition: all 0.3s ease;
          color: var(--bs-primary);
          text-decoration: none;
          font-size: 1rem;
        }

        .partner-card:hover .partner-website-link {
          opacity: 1;
          transform: translateY(0);
        }

        .partner-website-link:hover {
          background: var(--bs-primary);
          color: white;
          transform: translateY(0) scale(1.1);
        }

        /* Partners grid layout */
        .partners-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
          gap: 1.5rem;
        }

        /* Responsive adjustments */
        @media (max-width: 992px) {
          .partner-logo-item {
            width: calc(50% - 12px) !important;
          }
        }

        @media (max-width: 768px) {
          .hero-title {
            font-size: 2.5rem;
          }

          .hero-text {
            font-size: 1.1rem;
          }

          .cta-btn, .cta-btn-outline {
            padding: 0.75rem 2rem !important;
            font-size: 0.9rem;
          }

          .counter {
            font-size: 1.5rem;
          }
          
          .partner-logo-item {
            width: calc(50% - 12px) !important;
          }

          .partner-logo-card {
            min-height: 160px;
          }

          .partner-logo-img {
            max-height: 70px;
          }

          .partner-name-text {
            font-size: 0.85rem;
          }
        }

        @media (max-width: 576px) {
          .partner-logo-item {
            width: calc(100% - 12px) !important;
          }

          .partner-logo-card {
            min-height: 140px;
          }

          .partner-logo-img {
            max-height: 60px;
          }

          .partner-name-text {
            font-size: 0.8rem;
          }
        }
      `}</style>

      {/* Enhanced Hero Section */}
      <section className="hero-section position-relative text-white">
        {/* Background Image Slider */}
        <div className="hero-slider">
          {heroImages.map((image, index) => (
            <div
              key={index}
              className={`hero-slide ${index === currentSlide ? 'active' : ''}`}
              style={{
                backgroundImage: `url(${image})`,
                opacity: index === currentSlide ? 1 : 0,
                transform: index === currentSlide ? 'scale(1)' : 'scale(1.1)',
              }}
            />
          ))}
          
          {/* Overlay */}
          <div 
            className="position-absolute top-0 start-0 w-100 h-100"
            style={{
              background: 'linear-gradient(135deg, rgba(13, 110, 253, 0.85) 0%, rgba(108, 117, 125, 0.75) 100%)'
            }}
          />
        </div>

        {/* Content */}
        <div className="container position-relative" style={{ zIndex: 2 }}>
          <div className="row min-vh-100 align-items-center py-5">
            <div className="col-lg-8">
              <div className={`hero-content ${isVisible ? 'animate-in' : ''}`}>
                {/* Subtitle */}
                <div className="mb-3 subtitle-badge">
                  <span className="badge bg-light text-primary px-3 py-2 fs-6 fw-normal">
                    <i className="bi bi-heart-fill me-2"></i>
                    Empowering Communities
                  </span>
                </div>

                {/* Main Heading */}
                <h1 className="display-2 fw-bold mb-4 hero-title">
                  WELCOME TO
                  <span className="d-block text-gradient mt-2">
                    SIKOM
                  </span>
                </h1>

                {/* Description */}
                <p className="lead mb-5 fs-3 hero-text" style={{ maxWidth: '600px' }}>
                  Together let's build lasting peace for sustainable development
                </p>

                {/* CTA Buttons */}
                <div className="d-flex flex-wrap gap-3 hero-buttons">
                  <Link 
                    to="/about" 
                    className="btn btn-light btn-lg px-5 py-3 shadow-lg cta-btn"
                  >
                    <span>Explore More</span>
                    <i className="bi bi-arrow-right ms-2"></i>
                  </Link>
                  <Link 
                    to="/donate" 
                    className="btn btn-outline-light btn-lg px-5 py-3 cta-btn-outline"
                  >
                    <i className="bi bi-heart-fill me-2"></i>
                    <span>Donate Now</span>
                  </Link>
                </div>

                {/* Mini Stats */}
                <div className="row mt-5 pt-4 hero-stats">
                  {(Array.isArray(stats) ? stats : defaultStats).slice(0, 4).map((stat, index) => (
                    <div className="col-6 col-md-3 mb-3 mb-md-0" key={index}>
                      <div className="stat-item">
                        <h3 className="fw-bold mb-1 counter">{stat.value}</h3>
                        <p className="mb-0 small opacity-90">{stat.title}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Slider Indicators */}
        <div 
          className="position-absolute bottom-0 start-50 translate-middle-x mb-4"
          style={{ zIndex: 3 }}
        >
          <div className="d-flex gap-2">
            {heroImages.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`slider-dot ${index === currentSlide ? 'active' : ''}`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Scroll Indicator */}
        <div 
          className="position-absolute bottom-0 start-50 translate-middle-x mb-5 pb-3"
          style={{ zIndex: 3 }}
        >
          <div className="scroll-indicator">
            <i className="bi bi-chevron-down"></i>
          </div>
        </div>
      </section>

      {/* About Section */}
      {aboutSection.enabled && (
        <section className="py-5 bg-white position-relative overflow-hidden">
          <div className="container">
            {aboutSection.image ? (
              <div className="row align-items-center g-4">
                <div className="col-lg-6 order-lg-2">
                  <div className="about-image-wrapper position-relative">
                    <img 
                      src={aboutSection.image.startsWith('http') ? aboutSection.image : `${STATIC_BASE_URL}/${aboutSection.image}`}
                      alt={aboutSection.title}
                      className="img-fluid rounded-4 shadow-lg"
                      style={{ maxHeight: '500px', objectFit: 'cover', width: '100%' }}
                    />
                    <div className="position-absolute top-0 start-0 w-100 h-100 bg-primary bg-opacity-10 rounded-4"></div>
                  </div>
                </div>
                <div className="col-lg-6 order-lg-1">
                  <div className="pe-lg-4">
                    <span className="badge bg-primary bg-opacity-10 text-primary px-3 py-2 mb-3">
                      <i className="bi bi-info-circle me-2"></i>Who We Are
                    </span>
                    <h2 className="display-5 fw-bold mb-3">{aboutSection.title}</h2>
                    <h3 className="h4 text-primary mb-4">{aboutSection.subtitle}</h3>
                    <p className="lead text-muted mb-4" style={{ lineHeight: '1.8' }}>
                      {aboutSection.content}
                    </p>
                    <div className="d-flex flex-wrap gap-3">
                      <Link to="/about" className="btn btn-primary btn-lg px-4">
                        Learn More <i className="bi bi-arrow-right ms-2"></i>
                      </Link>
                      <Link to="/what-we-do" className="btn btn-outline-primary btn-lg px-4">
                        Our Work <i className="bi bi-briefcase ms-2"></i>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="row justify-content-center">
                <div className="col-lg-10 col-xl-8">
                  <div className="text-center">
                    <span className="badge bg-primary bg-opacity-10 text-primary px-3 py-2 mb-3">
                      <i className="bi bi-info-circle me-2"></i>Who We Are
                    </span>
                    <h2 className="display-5 fw-bold mb-3">{aboutSection.title}</h2>
                    <h3 className="h4 text-primary mb-4">{aboutSection.subtitle}</h3>
                    <p className="lead text-muted mb-4" style={{ lineHeight: '1.8', maxWidth: '800px', margin: '0 auto' }}>
                      {aboutSection.content}
                    </p>
                    <div className="d-flex flex-wrap justify-content-center gap-3">
                      <Link to="/about" className="btn btn-primary btn-lg px-4">
                        Learn More <i className="bi bi-arrow-right ms-2"></i>
                      </Link>
                      <Link to="/what-we-do" className="btn btn-outline-primary btn-lg px-4">
                        Our Work <i className="bi bi-briefcase ms-2"></i>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Mission, Vision, Values */}
      <section className="py-5 bg-gradient-light position-relative overflow-hidden">
        <div className="container position-relative" style={{ zIndex: 1 }}>
          <div className="row g-4">
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-lg mvv-card bg-white">
                <div className="card-body text-center p-5">
                  <div className="mb-4">
                    <div className="d-inline-flex align-items-center justify-content-center rounded-circle bg-primary bg-opacity-10" style={{ width: '80px', height: '80px' }}>
                      <i className="bi bi-bullseye display-4 text-primary"></i>
                    </div>
                  </div>
                  <h3 className="h4 mb-3 fw-bold">{mission.title || 'Our Mission'}</h3>
                  <p className="mb-0 text-muted" style={{ lineHeight: '1.8' }}>
                    {mission.content || 'To integrate indigenous knowledge and innovative solutions to achieving sustainable development in East Africa.'}
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-lg mvv-card bg-white">
                <div className="card-body text-center p-5">
                  <div className="mb-4">
                    <div className="d-inline-flex align-items-center justify-content-center rounded-circle bg-success bg-opacity-10" style={{ width: '80px', height: '80px' }}>
                      <i className="bi bi-eye display-4 text-success"></i>
                    </div>
                  </div>
                  <h3 className="h4 mb-3 fw-bold">{vision.title || 'Our Vision'}</h3>
                  <p className="mb-0 text-muted" style={{ lineHeight: '1.8' }}>
                    {vision.content || 'Wholistic development for healthy and peaceful co-existence in East Africa.'}
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-lg mvv-card bg-white">
                <div className="card-body text-center p-5">
                  <div className="mb-4">
                    <div className="d-inline-flex align-items-center justify-content-center rounded-circle bg-warning bg-opacity-10" style={{ width: '80px', height: '80px' }}>
                      <i className="bi bi-heart display-4 text-warning"></i>
                    </div>
                  </div>
                  <h3 className="h4 mb-3 fw-bold">Core Values</h3>
                  <ul className="list-unstyled text-start">
                    {values.map((value, index) => (
                      <li className="mb-3 d-flex align-items-start" key={index}>
                        <i className="bi bi-check-circle-fill text-success me-2 mt-1"></i> 
                        <span className="text-muted">{value}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Thematic Areas */}
      <section className="py-5 bg-white">
        <div className="container">
          <div className="row mb-5">
            <div className="col-12 text-center">
              <span className="badge bg-primary bg-opacity-10 text-primary px-3 py-2 mb-3">
                <i className="bi bi-layers me-2"></i>Our Focus
              </span>
              <h2 className="display-5 fw-bold mb-3">Our Thematic Areas</h2>
              <p className="lead text-muted">Focus areas driving our development work</p>
            </div>
          </div>
          <div className="row g-4">
            {thematicAreas.length > 0 ? (
              thematicAreas.map((area) => (
                <div className="col-md-4" key={area.id}>
                  <div className="card h-100 border-0 shadow-lg thematic-card bg-white">
                    <div className="card-body text-center p-5">
                      <div className="mb-4">
                        <div className="d-inline-flex align-items-center justify-content-center rounded-circle bg-primary bg-opacity-10" style={{ width: '80px', height: '80px' }}>
                          <i className={`bi ${area.icon || 'bi-card-checklist'} display-4 text-primary`}></i>
                        </div>
                      </div>
                      <h3 className="h5 mb-3 fw-bold">{area.title}</h3>
                      <p className="mb-4 text-muted" style={{ lineHeight: '1.7' }}>{area.short_description || area.description}</p>
                      <Link 
                        to={`/what-we-do#${area.slug || area.title.toLowerCase().replace(/\s+/g, '-')}`} 
                        className="btn btn-outline-primary"
                      >
                        Learn More <i className="bi bi-arrow-right ms-2"></i>
                      </Link>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-12 text-center py-5">
                <i className="bi bi-folder-x display-1 text-muted mb-3"></i>
                <p className="lead text-muted">Thematic areas coming soon...</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Impact Stats */}
      <section className="py-5 bg-gradient-primary text-white position-relative overflow-hidden">
        <div className="position-absolute top-0 start-0 w-100 h-100" style={{
          background: 'linear-gradient(135deg, rgba(13, 110, 253, 0.95) 0%, rgba(108, 117, 125, 0.9) 100%)',
          zIndex: 0
        }}></div>
        <div className="container position-relative" style={{ zIndex: 1 }}>
          <div className="row text-center mb-4">
            <div className="col-12">
              <h2 className="display-5 fw-bold mb-3">Our Impact</h2>
              <p className="lead opacity-90">Making a difference in communities across East Africa</p>
            </div>
          </div>
          <div className="row text-center">
            {(Array.isArray(stats) ? stats : defaultStats).map((stat, index) => (
              <div className="col-md-4 col-6 mb-4" key={index}>
                <div className="p-4 stat-counter">
                  <div className="mb-3">
                    <i className={`bi ${stat.icon} display-3`}></i>
                  </div>
                  <h2 className="display-4 fw-bold mb-2">{stat.value}</h2>
                  <h5 className="fw-normal opacity-90">{stat.title}</h5>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Latest Updates */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="row mb-5">
            <div className="col-12 text-center">
              <span className="badge bg-primary bg-opacity-10 text-primary px-3 py-2 mb-3">
                <i className="bi bi-newspaper me-2"></i>News & Updates
              </span>
              <h2 className="display-5 fw-bold mb-3">Latest Updates</h2>
              <p className="lead text-muted">Stay informed with our latest news and activities</p>
            </div>
          </div>
          <div className="row g-4">
            {updates.length > 0 ? (
              updates.map((update) => (
                <div className="col-md-4" key={update.id}>
                  <div className="card h-100 border-0 shadow-lg update-card bg-white">
                    <div className="card-body p-5">
                      <span className="badge bg-primary mb-3 px-3 py-2">
                        {update.type || 'Update'}
                      </span>
                      <h5 className="card-title mb-3 fw-bold">{update.title}</h5>
                      <p className="card-text text-muted mb-4" style={{ lineHeight: '1.7' }}>
                        {update.excerpt || (update.content ? update.content.substring(0, 150) + '...' : 'Read more about this update...')}
                      </p>
                      <Link 
                        to={`/updates/${update.slug || update.id}`} 
                        className="btn btn-outline-primary"
                      >
                        Read More <i className="bi bi-arrow-right ms-2"></i>
                      </Link>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-12 text-center py-5">
                <i className="bi bi-newspaper display-1 text-muted mb-3"></i>
                <p className="lead text-muted">No updates available at the moment</p>
              </div>
            )}
          </div>
          {updates.length > 0 && (
            <div className="text-center mt-5">
              <Link to="/updates" className="btn btn-primary btn-lg px-5">
                View All Updates <i className="bi bi-arrow-right ms-2"></i>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-5 bg-gradient-primary text-white position-relative overflow-hidden">
        <div className="position-absolute top-0 start-0 w-100 h-100" style={{
          background: 'linear-gradient(135deg, rgba(13, 110, 253, 0.95) 0%, rgba(108, 117, 125, 0.9) 100%)',
          zIndex: 0
        }}></div>
        <div className="container text-center position-relative" style={{ zIndex: 1 }}>
          <h2 className="display-5 fw-bold mb-4">Join Us in Making a Difference</h2>
          <p className="lead mb-5 opacity-90" style={{ maxWidth: '600px', margin: '0 auto 2rem' }}>
            Your support helps us build peaceful and resilient communities
          </p>
          <div className="d-flex flex-wrap justify-content-center gap-3">
            <Link to="/donate" className="btn btn-light btn-lg px-5 py-3 shadow-lg">
              <i className="bi bi-heart-fill me-2"></i>
              Donate Now
            </Link>
            <Link to="/contact" className="btn btn-outline-light btn-lg px-5 py-3">
              <i className="bi bi-envelope me-2"></i>
              Contact Us
            </Link>
          </div>
        </div>
      </section>

    {/* Partners Section - Scrolling Carousel */}
    <section className="py-5 partners-section bg-light">
      <div className="container">
        <div className="row mb-5">
          <div className="col-12 text-center">
            <h2 className="display-5 fw-bold mb-3">Our Valued Partners</h2>
            <p className="lead text-muted">
              Together with our partners, we create lasting impact in communities
            </p>
          </div>
        </div>
        
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading partners...</span>
            </div>
            <p className="lead text-muted mt-3">Loading our valued partners...</p>
          </div>
        ) : partners && partners.length > 0 ? (
          <div className="partners-carousel-wrapper position-relative">
            <div className="partners-carousel-container overflow-hidden">
              <div className="partners-carousel-scroll d-flex gap-4">
                {/* First set of partners */}
                {partners.map((partner, index) => {
                  const typeInfo = getPartnerTypeInfo(partner.type || 'implementing');
                  const logoUrl = fixLogoPath(partner.logo_path);
                  
                  return (
                    <div 
                      key={`partner-${partner.id || partner.name}-${index}`}
                      className="partner-logo-item flex-shrink-0"
                      style={{ width: 'calc(25% - 12px)' }}
                    >
                      <div className="partner-logo-card bg-white rounded-4 shadow-sm p-4 h-100 d-flex flex-column align-items-center justify-content-center">
                        <div className="partner-logo-wrapper mb-3 d-flex align-items-center justify-content-center" style={{ minHeight: '80px' }}>
                          {logoUrl ? (
                            <img
                              src={logoUrl}
                              alt={partner.name}
                              className="partner-logo-img"
                              onError={(e) => { e.target.src = '/images/placeholder-partner.png'; }}
                            />
                          ) : (
                            <div className="partner-logo-placeholder d-flex align-items-center justify-content-center">
                              <i className={`bi ${typeInfo.icon} display-4 text-${typeInfo.color}`}></i>
                            </div>
                          )}
                        </div>
                        <h6 className="partner-name-text text-center mb-0 fw-semibold" style={{ fontSize: '0.9rem', lineHeight: '1.3' }}>
                          {partner.name}
                        </h6>
                      </div>
                    </div>
                  );
                })}
                {/* Duplicate set for seamless loop */}
                {partners.map((partner, index) => {
                  const typeInfo = getPartnerTypeInfo(partner.type || 'implementing');
                  const logoUrl = fixLogoPath(partner.logo_path);
                  
                  return (
                    <div 
                      key={`partner-duplicate-${partner.id || partner.name}-${index}`}
                      className="partner-logo-item flex-shrink-0"
                      style={{ width: 'calc(25% - 12px)' }}
                    >
                      <div className="partner-logo-card bg-white rounded-4 shadow-sm p-4 h-100 d-flex flex-column align-items-center justify-content-center">
                        <div className="partner-logo-wrapper mb-3 d-flex align-items-center justify-content-center" style={{ minHeight: '80px' }}>
                          {logoUrl ? (
                            <img
                              src={logoUrl}
                              alt={partner.name}
                              className="partner-logo-img"
                              onError={(e) => { e.target.src = '/images/placeholder-partner.png'; }}
                            />
                          ) : (
                            <div className="partner-logo-placeholder d-flex align-items-center justify-content-center">
                              <i className={`bi ${typeInfo.icon} display-4 text-${typeInfo.color}`}></i>
                            </div>
                          )}
                        </div>
                        <h6 className="partner-name-text text-center mb-0 fw-semibold" style={{ fontSize: '0.9rem', lineHeight: '1.3' }}>
                          {partner.name}
                        </h6>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-5">
            <i className="bi bi-people display-1 text-muted mb-3"></i>
            <p className="lead text-muted">No partners information available at the moment</p>
          </div>
        )}
      </div>
    </section>
    </>
  );
};

export default Home;